import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { applicationListPath, useApi } from "../api/client";
import type { ApplicationListItem, ApplicationListResponse } from "../api/types";
import { ConfidenceBadge } from "../components/ConfidenceBadge";
import { CostCell } from "../components/CostCell";
import { Pagination } from "../components/Pagination";
import { Bar, Empty, ErrorState, Loading } from "../components/States";
import { SubmitterFilter } from "../components/SubmitterFilter";
import { Breadcrumb, CustomRange, SegmentedControl, Toolbar } from "../components/Toolbar";
import { downloadCsv, stampedName, toCsv } from "../lib/csv";
import { fmtCost, fmtDate, fmtTokens } from "../lib/format";
import { PERIOD_SEGMENTS, type PeriodKey } from "./DocumentList";

export function ApplicationList() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const page = Number(params.get("page") ?? 1);
  const pageSize = Number(params.get("page_size") ?? 25);
  const period = (params.get("period") ?? "all") as PeriodKey;
  const dateFrom = params.get("date_from") ?? "";
  const dateTo = params.get("date_to") ?? "";
  const submittedBy = params.get("submitted_by") ?? "";

  const [q, setQ] = useState(params.get("q") ?? "");

  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (q) next.set("q", q);
      else next.delete("q");
      if (next.get("q") !== params.get("q")) {
        next.set("page", "1");
        setParams(next, { replace: true });
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.set("page", "1");
    setParams(next);
  };

  const ready = period !== "custom" || (dateFrom !== "" && dateTo !== "");

  const { data, error, loading, reload } = useApi<ApplicationListResponse>(
    ready
      ? applicationListPath({
          page,
          page_size: pageSize,
          q: params.get("q") || undefined,
          period,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          submitted_by: submittedBy || undefined,
        })
      : null
  );

  const maxTokens = Math.max(1, ...(data?.items ?? []).map((a) => a.total_tokens));

  const exportCsv = () => {
    if (!data?.items.length) return;
    downloadCsv(
      stampedName("applications"),
      toCsv<ApplicationListItem>(data.items, [
        { header: "Business name", value: (r) => r.business_name },
        { header: "Application ID", value: (r) => r.application_id },
        { header: "Tenant", value: (r) => r.tenant_id },
        { header: "Documents", value: (r) => r.documents },
        { header: "Document types", value: (r) => r.distinct_types },
        { header: "Avg confidence %", value: (r) => r.avg_confidence },
        { header: "Tokens", value: (r) => r.total_tokens },
        { header: "Cost USD", value: (r) => r.cost_usd },
        { header: "Cost complete", value: (r) => r.cost_complete },
        { header: "First seen", value: (r) => r.first_seen },
        { header: "Last seen", value: (r) => r.last_seen },
      ])
    );
  };

  return (
    <>
      <Breadcrumb trail={[{ label: "Agents", to: "/agents" }, { label: "Applications" }]} />

      <div className="page-head">
        <div>
          <h1 className="page-title">Applications</h1>
          <p className="page-sub">
            Documents grouped by the application they were submitted under.
          </p>
        </div>
        <div className="range-group">
          <SubmitterFilter
            value={submittedBy}
            onChange={(v) => setParam("submitted_by", v)}
            idPrefix="app"
          />
          {period === "custom" ? (
            <CustomRange
              from={dateFrom}
              to={dateTo}
              onFrom={(v) => setParam("date_from", v)}
              onTo={(v) => setParam("date_to", v)}
              idPrefix="app"
            />
          ) : null}
          <SegmentedControl
            segments={PERIOD_SEGMENTS}
            value={period}
            onChange={(p) => {
              const next = new URLSearchParams(params);
              next.set("period", p);
              if (p !== "custom") {
                next.delete("date_from");
                next.delete("date_to");
              }
              next.set("page", "1");
              setParams(next);
            }}
            label="Time range"
          />
        </div>
      </div>

      <Toolbar
        title="Application Net Usage Summary"
        subtitle="Token and cost rollup per application"
        onExport={exportCsv}
        exportDisabled={!data?.items.length}
        search={q}
        onSearch={setQ}
        searchPlaceholder="Search by business name or application id"
      />

      <div className="card">
        {!ready ? (
          <Empty title="Pick both dates" hint="A custom range needs a start and an end." />
        ) : loading ? (
          <Loading what="applications" />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : !data || data.items.length === 0 ? (
          <Empty
            title="No applications in this period"
            hint="Documents submitted without an application_id are not grouped here."
          />
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col" className="si">Sl</th>
                    <th scope="col">Application</th>
                    <th scope="col" className="num">Documents</th>
                    <th scope="col">Avg confidence</th>
                    <th scope="col" className="num">Tokens</th>
                    <th scope="col" style={{ width: 120 }}>Share</th>
                    <th scope="col" className="num">Cost</th>
                    <th scope="col">Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((a, i) => {
                    const open = () => navigate(`/applications/${a.application_id}`);
                    return (
                      <tr
                        key={a.application_id}
                        className="row-link"
                        onClick={open}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            open();
                          }
                        }}
                        tabIndex={0}
                        role="link"
                        aria-label={`Open application ${a.business_name ?? a.application_id}`}
                      >
                        <td className="si">{(data.page - 1) * data.page_size + i + 1}</td>
                        <td>
                          {/* The name leads where there is one, because that is
                              what a reviewer recognises; the id stays on the
                              row underneath it, because that is what they paste
                              into a ticket. An unnamed application keeps the id
                              in the lead position rather than showing a blank
                              line above it. */}
                          {a.business_name ? (
                            <>
                              <div className="strong">{a.business_name}</div>
                              <div className="mono muted">{a.application_id}</div>
                            </>
                          ) : (
                            <div className="mono">{a.application_id}</div>
                          )}
                          <div className="muted">
                            {a.distinct_types} document type{a.distinct_types === 1 ? "" : "s"}
                            {a.tenant_id ? ` · ${a.tenant_id}` : ""}
                          </div>
                        </td>
                        <td className="num">{fmtTokens(a.documents)}</td>
                        <td><ConfidenceBadge value={a.avg_confidence} /></td>
                        <td className="num">{fmtTokens(a.total_tokens)}</td>
                        <td><Bar value={a.total_tokens} max={maxTokens} /></td>
                        <td className="num">
                          <CostCell
                            usd={a.cost_usd}
                            recorded={a.documents_without_usage < a.documents}
                          />
                          {!a.cost_complete && a.documents_without_usage < a.documents ? (
                            <div
                              className="muted"
                              title={`${a.documents_without_usage} of ${a.documents} documents have no usage recorded.`}
                            >
                              partial
                            </div>
                          ) : null}
                        </td>
                        <td className="nowrap muted">{fmtDate(a.last_seen)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row" colSpan={2}>This page</th>
                    <td className="num">
                      <strong>{fmtTokens(data.items.reduce((s, a) => s + a.documents, 0))}</strong>
                    </td>
                    <td />
                    <td className="num">
                      <strong>{fmtTokens(data.items.reduce((s, a) => s + a.total_tokens, 0))}</strong>
                    </td>
                    <td />
                    <td className="num">
                      <strong>
                        {fmtCost(
                          data.items.some((a) => a.cost_usd !== null)
                            ? data.items.reduce((s, a) => s + (a.cost_usd ?? 0), 0)
                            : null
                        )}
                      </strong>
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>

            <Pagination
              page={data.page}
              totalPages={data.total_pages}
              total={data.total}
              pageSize={data.page_size}
              noun="applications"
              onPage={(p) => setParam("page", String(p))}
              onPageSize={(n) => {
                const next = new URLSearchParams(params);
                next.set("page_size", String(n));
                next.set("page", "1");
                setParams(next);
              }}
            />
          </>
        )}
      </div>
    </>
  );
}
